/**
 * "Synchronisation des données de base" — what the portal exchanges, and what
 * still needs a person.
 *
 * Three decisions shape this screen, and each is about what an operator
 * actually needs to know:
 *
 * **It says when there is no connector, and does not treat it as a fault.**
 * An installation with none is complete: everything is created in the back
 * office, and the mapping, journal and queue all work. The eyebrow says so
 * rather than the page showing four zeros and an error tone.
 *
 * **The direction is editable here, entity by entity.** It is the decision
 * that matters most on this page — who owns a field, the portal or the
 * external system — and sending an operator to a second screen to change it
 * would hide the one control the table exists to offer.
 *
 * **A refusal is not an error.** A locked field the external system tried to
 * overwrite produced a successful write of everything else; the journal line
 * carries the refused fields, and the anomaly column lists them. Rendering
 * them as failures would make an operator hunt for a breakage that is
 * actually the lock doing its job.
 */

import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { EM_DASH, moment } from "@hardauto/core-i18n";
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Page,
  Select,
  Stat,
  StatGrid,
  StatusDot,
  useToast,
  type BadgeTone,
  type Column,
} from "@hardauto/core-ui";

import {
  IntegrationApi,
  type Direction,
  type EntityContract,
  type JournalEntry,
  type QueuedChange,
  type SyncOutcome,
} from "../api";
import { NAMESPACE } from "../i18n";
import { INTEGRATION_PATHS } from "../paths";
import "./integration.css";

/** Every direction, in the order the mapping screen's select offers them. */
const DIRECTIONS: readonly Direction[] = ["in", "out", "both", "local"];

/**
 * How an outcome reads at a glance.
 *
 * `unchanged` is neutral rather than positive on purpose: an external system
 * re-sending 40 000 identical rows every night is worth noticing, and a green
 * badge would say "all is well" about a run that did nothing.
 */
const OUTCOME_TONE: Record<SyncOutcome, BadgeTone> = {
  ok: "success",
  unchanged: "neutral",
  refused: "warning",
  failed: "danger",
};

export function SyncScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new IntegrationApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayMap = useCan("integration.mapping.write");
  const mayReplay = useCan("integration.queue.replay");

  const overviewFetcher = useCallback(() => api.overview(), [api]);
  const overview = useResource(overviewFetcher);

  const journalFetcher = useCallback(() => api.journal({ limit: 40 }), [api]);
  const journal = useResource(journalFetcher);

  const anomaliesFetcher = useCallback(
    () => api.journal({ anomalies_only: true, limit: 20 }),
    [api],
  );
  const anomalies = useResource(anomaliesFetcher);

  const queueFetcher = useCallback(() => api.queue({ limit: 30 }), [api]);
  const queue = useResource(queueFetcher);

  const counts = useMemo(() => {
    const table = new Map<SyncOutcome, number>();
    for (const entry of overview.data?.outcomes ?? []) table.set(entry.outcome, entry.count);
    return table;
  }, [overview.data]);

  const entityColumns: Column<EntityContract>[] = [
    {
      key: "entity",
      header: t("sync.column.entity"),
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.label}</div>
          <code className="hardauto-code">{row.code}</code>
        </div>
      ),
    },
    {
      key: "direction",
      header: t("sync.column.direction"),
      render: (row) =>
        mayMap ? (
          <Select
            className="hardauto-cell-select"
            aria-label={`${t("sync.column.direction")} — ${row.label}`}
            value={row.direction}
            disabled={action.isBusy(row.code)}
            onChange={(event) => {
              const direction = event.target.value as Direction;
              void action.run(row.code, async () => {
                try {
                  await api.updateEntity(row.code, { direction });
                  overview.reload();
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              });
            }}
          >
            {DIRECTIONS.map((value) => (
              <option key={value} value={value}>
                {t(`direction.${value}`)}
              </option>
            ))}
          </Select>
        ) : (
          <Badge tone="neutral">{t(`direction.${row.direction}`)}</Badge>
        ),
    },
    {
      key: "key",
      header: t("sync.column.key"),
      render: (row) => <code className="hardauto-code">{row.external_key_field || EM_DASH}</code>,
    },
    {
      key: "fields",
      header: t("sync.column.fields"),
      render: (row) =>
        row.field_count === 0 ? (
          <span className="hardauto-hint">{t("sync.passthrough")}</span>
        ) : (
          <Link to={INTEGRATION_PATHS.entityMapping} params={{ entity: row.code }}>
            {t("sync.fieldCount", { count: row.field_count })}
          </Link>
        ),
    },
    {
      key: "lastIn",
      header: t("sync.column.lastIn"),
      render: (row) => moment(row.last_inbound_at, i18n.language, t("common.never")),
    },
    {
      key: "lastOut",
      header: t("sync.column.lastOut"),
      render: (row) => moment(row.last_outbound_at, i18n.language, t("common.never")),
    },
    {
      key: "state",
      header: t("sync.column.state"),
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <StatusDot
            label={row.is_enabled ? t("sync.enabled") : t("sync.disabled")}
            tone={row.is_enabled ? "success" : "neutral"}
          />
          {mayMap ? (
            <Button
              variant="ghost"
              size="sm"
              busy={action.isBusy(`toggle:${row.code}`)}
              busyLabel={t("common.saving")}
              onClick={() =>
                void action.run(`toggle:${row.code}`, async () => {
                  try {
                    await api.updateEntity(row.code, { is_enabled: !row.is_enabled });
                    overview.reload();
                  } catch {
                    toast.show(t("error.title"), { tone: "danger" });
                  }
                })
              }
            >
              {row.is_enabled ? t("sync.toggleOff") : t("sync.toggleOn")}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const queueColumns: Column<QueuedChange>[] = [
    { key: "entity", header: t("sync.queueColumn.entity"), render: (row) => row.entity },
    { key: "operation", header: t("sync.queueColumn.operation"), render: (row) => row.operation },
    {
      key: "state",
      header: t("sync.queueColumn.state"),
      render: (row) => (
        <Badge tone={row.state === "failed" ? "danger" : "neutral"}>
          {t(`queueState.${row.state}`)}
        </Badge>
      ),
    },
    {
      key: "attempts",
      header: t("sync.queueColumn.attempts"),
      render: (row) => String(row.attempts),
    },
    {
      key: "error",
      header: t("sync.queueColumn.error"),
      render: (row) => <span className="hardauto-hint">{row.last_error || EM_DASH}</span>,
    },
    {
      key: "created",
      header: t("sync.queueColumn.created"),
      render: (row) => moment(row.created_at, i18n.language),
    },
    {
      key: "replay",
      header: "",
      render: (row) =>
        mayReplay && row.state === "failed" ? (
          <Button
            variant="ghost"
            size="sm"
            busy={action.isBusy(`replay:${row.id}`)}
            busyLabel={t("common.saving")}
            onClick={() =>
              void action.run(`replay:${row.id}`, async () => {
                try {
                  await api.replay(row.id);
                  toast.show(t("sync.replayed"));
                  queue.reload();
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              })
            }
          >
            {t("sync.replay")}
          </Button>
        ) : null,
    },
  ];

  const connector = overview.data?.connector;

  return (
    <Page
      title={t("sync.title")}
      description={t("sync.intro")}
      eyebrow={
        connector && !connector.is_absent
          ? `${t("sync.eyebrow")} · ${connector.label || connector.code}`
          : t("sync.eyebrowNoConnector")
      }
      actions={
        <Button
          variant="secondary"
          onClick={() => {
            overview.reload();
            journal.reload();
            anomalies.reload();
            queue.reload();
          }}
        >
          {t("common.refresh")}
        </Button>
      }
    >
      <StatGrid>
        <Stat label={t("sync.kpi.written")} value={String(counts.get("ok") ?? 0)} />
        <Stat label={t("sync.kpi.unchanged")} value={String(counts.get("unchanged") ?? 0)} />
        <Stat
          label={t("sync.kpi.anomalies")}
          value={String(overview.data?.anomalies_24h ?? 0)}
          tone={(overview.data?.anomalies_24h ?? 0) > 0 ? "warning" : "neutral"}
        />
        <Stat
          label={t("sync.kpi.queue")}
          value={String(overview.data?.queue_pending ?? 0)}
          tone={(overview.data?.queue_failed ?? 0) > 0 ? "danger" : "neutral"}
        />
      </StatGrid>

      <Card title={t("sync.entities")} description={t("sync.entitiesIntro")}>
        <DataTable<EntityContract>
          caption={t("sync.entities")}
          columns={entityColumns}
          rows={overview.data?.entities ?? []}
          rowKey={(row) => row.code}
          loading={overview.loading}
          loadingLabel={t("sync.entities")}
          error={
            overview.failed
              ? { title: t("error.load"), onRetry: overview.reload, retryLabel: t("error.retry") }
              : undefined
          }
          empty={{ title: t("sync.entities"), description: t("sync.entitiesIntro") }}
        />
      </Card>

      <div className="hardauto-sync__split">
        <Card title={t("sync.journal")} description={t("sync.journalIntro")}>
          {journal.data && journal.data.items.length === 0 ? (
            <EmptyState title={t("sync.journalEmpty")} />
          ) : (
            <div className="hardauto-log">
              {(journal.data?.items ?? []).map((entry) => (
                <LogLine key={entry.id} entry={entry} locale={i18n.language} />
              ))}
            </div>
          )}
        </Card>

        <Card title={t("sync.anomalies")} description={t("sync.anomaliesIntro")}>
          {anomalies.data && anomalies.data.items.length === 0 ? (
            <EmptyState title={t("sync.anomaliesEmpty")} />
          ) : (
            <div className="hardauto-anomalies">
              {(anomalies.data?.items ?? []).map((entry) => (
                <article key={entry.id} className="hardauto-anomaly">
                  <div className="hardauto-anomaly__head">
                    <strong>{entry.entity}</strong>
                    <Badge tone={OUTCOME_TONE[entry.outcome]}>
                      {t(`outcome.${entry.outcome}`)}
                    </Badge>
                  </div>
                  <div>{entry.message || EM_DASH}</div>
                  {entry.refused_fields.length > 0 ? (
                    <p className="hardauto-anomaly__fields">
                      {t("sync.refused")} {entry.refused_fields.join(", ")}
                    </p>
                  ) : null}
                  <p className="hardauto-anomaly__fields">
                    {entry.external_ref || EM_DASH} · {moment(entry.at, i18n.language)}
                  </p>
                </article>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card title={t("sync.queue")} description={`${t("sync.queueIntro")} ${t("sync.replayHint")}`}>
        {queue.data && queue.data.items.length === 0 ? (
          <EmptyState title={t("sync.queueEmpty")} />
        ) : (
          <DataTable<QueuedChange>
            caption={t("sync.queue")}
            columns={queueColumns}
            rows={queue.data?.items ?? []}
            rowKey={(row) => row.id}
            loading={queue.loading}
            loadingLabel={t("sync.queue")}
            error={
              queue.failed
                ? { title: t("error.load"), onRetry: queue.reload, retryLabel: t("error.retry") }
                : undefined
            }
            empty={{ title: t("sync.queueEmpty") }}
          />
        )}
      </Card>
    </Page>
  );
}

/**
 * One line of the execution log.
 *
 * Its own component so the list does not re-render every line's formatting on
 * an unrelated state change, and so the columns are declared once — the
 * timestamp, the way, the outcome and the message, in that order, because that
 * is the order somebody scanning for last night's run reads them in.
 */
function LogLine({ entry, locale }: { entry: JournalEntry; locale: string }) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <div className="hardauto-log__line">
      <span className="hardauto-log__time">{moment(entry.at, locale)}</span>
      <span>{t(`way.${entry.way}`, entry.way)}</span>
      <Badge tone={OUTCOME_TONE[entry.outcome]}>{t(`outcome.${entry.outcome}`)}</Badge>
      <span>
        <strong>{entry.entity}</strong>{" "}
        <span className="hardauto-log__ref">{entry.external_ref}</span>{" "}
        {entry.message || (entry.written_fields.length > 0 ? entry.written_fields.join(", ") : "")}
      </span>
    </div>
  );
}
